import React, { useContext, useEffect, useReducer, useState } from "react";

import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { CheckCircle, Warning } from "@material-ui/icons";
import { DeleteOutline, Edit } from "@material-ui/icons";
import PromptModal from "../../components/PromptModal";
import toastUtils from "../../utils/toastUtils";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlanPermissions from "../../hooks/usePlanPermissions";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_PROMPTS") {
    const prompts = action.payload;
    const newPrompts = [];

    prompts.forEach((prompt) => {
      const promptIndex = state.findIndex((p) => p.id === prompt.id);
      if (promptIndex !== -1) {
        state[promptIndex] = prompt;
      } else {
        newPrompts.push(prompt);
      }
    });

    return [...state, ...newPrompts];
  }

  if (action.type === "UPDATE_PROMPTS") {
    const prompt = action.payload;
    const promptIndex = state.findIndex((p) => p.id === prompt.id);

    if (promptIndex !== -1) {
      state[promptIndex] = prompt;
      return [...state];
    } else {
      return [prompt, ...state];
    }
  }

  if (action.type === "DELETE_PROMPT") {
    const promptId = action.payload;
    const promptIndex = state.findIndex((p) => p.id === promptId);
    if (promptIndex !== -1) {
      state.splice(promptIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Prompts = () => {
  const classes = useStyles();

  const [prompts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  
  const { user, socket } = useContext(AuthContext);
  const { permissions } = usePlanPermissions();
  const history = useHistory();
  const companyId = user.companyId;

  useEffect(() => {
    if (!permissions.useOpenAi) {
      toastUtils.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.", 'Erro no Prompt');
      setTimeout(() => {
        history.push(`/`)
      }, 1000);
    }
  }, [permissions.useOpenAi, history]);

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prompt");
      dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // const socket = socketManager.GetSocket();

    const onPromptEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
      }
    };

    socket.on(`company-${companyId}-prompt`, onPromptEvent);
    return () => {
      socket.off(`company-${companyId}-prompt`, onPromptEvent);
    };
  }, [socket]);

  const handleOpenPromptModal = () => {
    setPromptModalOpen(true);
    setSelectedPrompt(null);
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleEditPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      const { data } = await api.delete(`/prompt/${promptId}`);
      toastUtils.info(i18n.t(data.message), 'Prompt Removido');
    } catch (err) {
      toastError(err);
    }
    setSelectedPrompt(null);
  };

  const handleConfirmDelete = () => {
    handleDeletePrompt(selectedPrompt.id);
    setConfirmModalOpen(false);
  };

  const openDeleteConfirmation = (prompt) => {
    setSelectedPrompt(prompt);
    setConfirmModalOpen(true);
  };

  const renderMainView = () => (
    <>
      <MainHeader>
        <Title>{i18n.t("prompts.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenPromptModal}
          >
            {i18n.t("prompts.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {renderPromptsTable()}
      </Paper>
    </>
  );


  // Helper function to determine prompt status
  const getPromptStatus = (prompt) => {
    // A prompt is considered "configured" if it has all essential fields
    const hasName = prompt.name && prompt.name.trim().length > 0;
    const hasPrompt = prompt.prompt && prompt.prompt.trim().length > 0;
    const hasModel = prompt.model && prompt.model.trim().length > 0;
    const hasQueue = prompt.queueId && prompt.queue;
    
    if (hasName && hasPrompt && hasModel && hasQueue) {
      return {
        label: i18n.t("prompts.status.active"),
        color: "primary",
        icon: <CheckCircle style={{ fontSize: 16 }} />
      };
    } else {
      return {
        label: i18n.t("prompts.status.incomplete"),
        color: "default",
        icon: <Warning style={{ fontSize: 16 }} />
      };
    }
  };

  const renderPromptsTable = () => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell align="left">
            {i18n.t("prompts.table.name")}
          </TableCell>
          <TableCell align="left">
            {i18n.t("prompts.table.queue")}
          </TableCell>
          <TableCell align="left">
            {i18n.t("prompts.table.model")}
          </TableCell>
          <TableCell align="left">
            {i18n.t("prompts.table.max_tokens")}
          </TableCell>
          <TableCell align="center">
            {i18n.t("prompts.table.status")}
          </TableCell>
          <TableCell align="center">
            {i18n.t("prompts.table.actions")}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <>
          {prompts.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("prompts.table.noPrompts")}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {prompts.map((prompt) => {
            const status = getPromptStatus(prompt);
            return (
              <TableRow key={prompt.id}>
                <TableCell align="left">
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {prompt.name}
                  </Typography>
                </TableCell>
                <TableCell align="left">
                  {prompt.queue?.name || (
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("prompts.table.noQueue")}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="left">
                  <Chip 
                    label={prompt.model || "gpt-3.5-turbo"} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="left">{prompt.maxTokens}</TableCell>
                <TableCell align="center">
                  <Tooltip title={status.label}>
                    <Chip
                      icon={status.icon}
                      label={status.label}
                      size="small"
                      color={status.color}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={i18n.t("prompts.buttons.edit")}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditPrompt(prompt)}
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("prompts.buttons.delete")}>
                    <IconButton
                      size="small"
                      onClick={() => openDeleteConfirmation(prompt)}
                      color="secondary"
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
          {loading && <TableRowSkeleton columns={6} />}
        </>
      </TableBody>
    </Table>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedPrompt &&
          `${i18n.t("prompts.confirmationModal.deleteTitle")} ${selectedPrompt.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={handleConfirmDelete}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
      />

      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        renderMainView()
      )}
    </MainContainer>
  );
};

export default Prompts;
